import React, { useMemo, useState } from 'react';
import './addWellTestModal.scss';
import ModalHeader from '../../components/common/ModalHeader';
import FormField from '../../components/common/FormField';
import CommentSection from '../../components/common/CommentSection';
import ActionButtons from '../../components/common/ActionButtons';
import { FormData } from './services/addWellTestService';
import { saveWellTestAsync } from './services/addWellTestSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { fetchWellTestData } from '../../dispatchers/WellTestDispatcher';
import { createPortal } from 'react-dom';
import { formatDateWithUtcOffset } from '../../utilities/DateUtils';

interface FormErrors {
  [key: string]: string;
}

interface TouchedFields {
  [key: string]: boolean;
}

interface AddWellTestModalProps {
  modalTitle: string;
  buttonText: string;
  onClose: () => void;
  initialData?: Partial<FormData>;
  disabled?: boolean;
  installNumber: number | null;
}

export default function AddWellTestModal({
  modalTitle,
  buttonText,
  onClose,
  initialData,
  disabled = false,
  installNumber,
}: AddWellTestModalProps) {
  const [formData, setFormData] = useState<FormData>({
    liquidFlowrate: '',
    waterCut: '',
    totalGasOilRatio: '',
    wellheadPressure: '',
    wellheadTemperature: '',
    casingPressure: '',
    flowlinePressure: '',
    productionChokePosition: '',
    pumpIntakePressure: '',
    pumpIntakeTemperature: '',
    pumpFrequency: '',
    pumpDischargePressure: '',
    waterDensity: '',
    comment: '',
    enabled: true,
    date: '',
    wellId: null,
    methodOfProduction: '',
    id: 0,
    ...initialData,
  });

  const dispatch = useAppDispatch();

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const testWellData = useAppSelector((state: any) => state.testWellDataSlice.data);
  const wellTestIdData = useAppSelector((state: any) => state.WellTestIdSlice?.data);
  const utcOffset = useAppSelector((state: any) => state.assetTimezone.data?.utcOffset || 0);

  const disabledDates = useMemo(
    () =>
      (testWellData ?? []).map((item: any) => {
        const date = new Date(item.Date);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }),
    [testWellData],
  );

  // List of mandatory fields
  const mandatoryFields = [
    'date',
    'liquidFlowrate',
    'waterCut',
    'totalGasOilRatio',
    'wellheadPressure',
    'waterDensity',
    'pumpIntakePressure',
    'pumpIntakeTemperature',
    'pumpFrequency',
  ];

  const validateField = (name: string, value: string): string => {
    // Check if field is mandatory and empty
    if (mandatoryFields.includes(name) && value === '') {
      return 'This field is required';
    }

    // If not empty, validate based on field-specific rules
    if (value === '') return '';

    const validateNumericField = (value: string, min: number, max: number, errorMessage: string): string | null => {
      if (!/^\d*\.?\d+$/.test(value)) {
        return errorMessage;
      }
      const numericValue = parseFloat(value);
      if (isNaN(numericValue) || numericValue < min || numericValue > max) {
        return errorMessage;
      }
      return null;
    };

    switch (name) {
      case 'liquidFlowrate':
        return validateNumericField(value, 0.1, 40000, 'Must be between 0.1 and 40000 stb/d') || '';
      case 'waterCut':
        return validateNumericField(value, 0, 100, 'Must be between 0 and 100 %') || '';
      case 'totalGasOilRatio':
        return validateNumericField(value, 0.000001, 75000, 'Must be between 0.000001 and 75000 scf/stb') || '';
      case 'wellheadPressure':
        return validateNumericField(value, 1, 3000, 'Must be between 1 and 3000 psig') || '';
      case 'wellheadTemperature':
        return validateNumericField(value, 32, 500, 'Must be between 32 and 500 deg F') || '';
      case 'casingPressure':
        return validateNumericField(value, 1, 5000, 'Must be between 1 and 5000 psig') || '';
      case 'flowlinePressure':
        return validateNumericField(value, 1, 3000, 'Must be between 1 and 3000 psig') || '';
      case 'productionChokePosition':
        return validateNumericField(value, 0, 100, 'Must be between 0 and 100 %') || '';
      case 'pumpIntakePressure':
        return validateNumericField(value, 1, 5000, 'Must be between 1 and 5000 psig') || '';
      case 'pumpIntakeTemperature':
        return validateNumericField(value, 32, 500, 'Must be between 32 and 500 deg F') || '';
      case 'pumpFrequency':
        return validateNumericField(value, 32, 500, 'Must be between 32 and 500 Hz') || '';
      case 'pumpDischargePressure':
        return validateNumericField(value, 1, 5000, 'Must be between 1 and 5000 psig') || '';
      case 'waterDensity':
        return validateNumericField(value, 0.39, 1.6, 'Must be between 0.39 and 1.6 stb/d') || '';
      default:
        return '';
    }
  };
  const handleInputChange = (name: string, value: string | number | Date | null) => {
    if (value === null || value instanceof Date) {
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });

    // Mark field as touched
    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true,
      });
    }

    const error = validateField(name, String(value));

    if (error) {
      setErrors({
        ...errors,
        [name]: error,
      });
    } else {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }

    if (name === 'date' && value) {
      const newErrors = { ...errors };
      delete newErrors.date;
      setErrors(newErrors);
    }
  };

  const formatValue = (value: string | number | null): string | null => {
    if (value === null) return null;
    return typeof value === 'number' ? value.toString() : value;
  };

  const handleBlur = (name: string) => {
    // Mark field as touched on blur
    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true,
      });

      // Validate on blur for mandatory fields
      if (mandatoryFields.includes(name) && formData[name as keyof typeof formData] === '') {
        setErrors({
          ...errors,
          [name]: 'This field is required',
        });
      }
    }
  };

  const handleCheckboxChange = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      enabled: !prevFormData.enabled,
    }));
  };

  const validateForm = () => {
    const newErrors = { ...errors };
    let isValid = true;

    // Check all mandatory fields
    mandatoryFields.forEach((field) => {
      if (formData[field as keyof typeof formData] === '') {
        newErrors[field] = 'This field is required';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const [isModalOpen, setIsModalOpen] = useState(true);

  const handleCancel = () => {
    setIsModalOpen(false);
    onClose();
  };

  const handleConfirmSave = async () => {
    // Mark all mandatory fields as touched
    const newTouched = { ...touched };
    mandatoryFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    // Validate the form before proceeding
    if (!validateForm()) {
      console.log('Form has errors, cannot submit');
      return;
    }

    setIsModalOpen(false);
    onClose();

    setSubmitError('');
    setSubmitSuccess(false);

    try {
      setIsSubmitting(true);

      const updatedFormData = {
        ...formData,
        date: formData.date ? formatDateWithUtcOffset(new Date(formData.date), utcOffset, true) : '',
        wellId: wellTestIdData?.WellId || null,
        methodOfProduction: wellTestIdData?.MethodOfProduction || null,
      };

      const resultAction = await dispatch(saveWellTestAsync(updatedFormData));

      if (saveWellTestAsync.fulfilled.match(resultAction)) {
        console.log('Form submitted successfully:', resultAction.payload);
        setSubmitSuccess(true);

        // Fetch updated data after successful save
        if (wellTestIdData) {
          dispatch(
            fetchWellTestData({
              wellId: wellTestIdData?.WellId,
              installNumber: installNumber,
            }),
          );
        }
      } else {
        console.error('Submission failed:', resultAction.payload);
        setSubmitError(resultAction.payload as string);
      }
    } catch (error) {
      console.error('Unexpected error during dispatch:', error);
      setSubmitError('Unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isModalOpen) {
    return null;
  }

  return createPortal(
    <div className='modal-overlay'>
      <div className='modal-container'>
        <div className='modal-main-container'>
          <ModalHeader title={modalTitle} onClose={handleCancel} />

          <main className='form-main'>
            <section className='form-section'>
              {submitSuccess && <div className='success-message'>Well test data saved successfully!</div>}

              {submitError && <div className='error-message'>{submitError}</div>}

              <div className='date-selector-container'>
                <FormField
                  name='date'
                  value={formData.date ? new Date(formData.date) : null}
                  onChange={(name, value) => {
                    if (value) {
                      const date = value as Date;
                      const formattedDateTime = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
                        date.getDate(),
                      ).padStart(
                        2,
                        '0',
                      )}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(
                        date.getSeconds(),
                      ).padStart(2, '0')}.${String(date.getMilliseconds()).slice(0, 1)}`;
                      handleInputChange(name, formattedDateTime);
                    } else {
                      handleInputChange(name, '');
                    }
                  }}
                  error={touched.date ? errors.date : ''}
                  type='date'
                  required={true}
                  disabled={disabled}
                  disabledDates={disabledDates}
                />
              </div>
              <div className='enabled-container'>
                <div className='checkbox-container'>
                  <div className={`checkbox ${formData.enabled ? 'enabled' : ''}`} onClick={handleCheckboxChange}>
                    {formData.enabled && <div className='checkbox-icon' />}
                  </div>
                </div>
                <div className='enabled-text-container' onClick={handleCheckboxChange}>
                  <span className='enabled-text'>Enabled</span>
                </div>
              </div>

              <div className='form-row'>
                <FormField
                  label='Total rate (stb/d)'
                  name='liquidFlowrate'
                  value={formatValue(formData.liquidFlowrate)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0.1 and 40000 stb/d'
                  error={touched.liquidFlowrate ? errors.liquidFlowrate : ''}
                  required={true}
                />
                <FormField
                  label='Water cut (%)'
                  name='waterCut'
                  value={formatValue(formData.waterCut)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0 and 100 %'
                  error={touched.waterCut ? errors.waterCut : ''}
                  required={true}
                />
                <FormField
                  label='Total gas oil ratio (scf/stb)'
                  name='totalGasOilRatio'
                  value={formatValue(formData.totalGasOilRatio)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0.000001 and 75000 scf/stb'
                  multilineHelpText={true}
                  error={touched.totalGasOilRatio ? errors.totalGasOilRatio : ''}
                  required={true}
                />
              </div>

              <div className='form-row'>
                <FormField
                  label='Tubing head pressure (psig)'
                  name='wellheadPressure'
                  value={formatValue(formData.wellheadPressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1 and 3000 psig'
                  error={touched.wellheadPressure ? errors.wellheadPressure : ''}
                  required={true}
                />
                <FormField
                  label='Wellhead temperature (deg F)'
                  name='wellheadTemperature'
                  value={formatValue(formData.wellheadTemperature)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 32 and 500 deg F'
                  error={touched.wellheadTemperature ? errors.wellheadTemperature : ''}
                />
                <FormField
                  label='Casing head pressure (psig)'
                  name='casingPressure'
                  value={formatValue(formData.casingPressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1 and 5000 psig'
                  error={touched.casingPressure ? errors.casingPressure : ''}
                />
              </div>

              <div className='form-row'>
                <FormField
                  label='Flowline pressure (psig)'
                  name='flowlinePressure'
                  value={formatValue(formData.flowlinePressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1 and 3000 psig'
                  error={touched.flowlinePressure ? errors.flowlinePressure : ''}
                />
                <FormField
                  label='Production choke position (%)'
                  name='productionChokePosition'
                  value={formatValue(formData.productionChokePosition)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0 and 100 %'
                  error={touched.productionChokePosition ? errors.productionChokePosition : ''}
                />
                <FormField
                  label='Pump intake pressure (psig)'
                  name='pumpIntakePressure'
                  value={formatValue(formData.pumpIntakePressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1 and 5000 psig'
                  error={touched.pumpIntakePressure ? errors.pumpIntakePressure : ''}
                  required={true}
                />
              </div>

              <div className='form-row'>
                <FormField
                  label='Pump intake temperature (deg F)'
                  name='pumpIntakeTemperature'
                  value={formatValue(formData.pumpIntakeTemperature)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 32 and 500 deg F'
                  error={touched.pumpIntakeTemperature ? errors.pumpIntakeTemperature : ''}
                  required={true}
                />
                <FormField
                  label='Frequency (Hz)'
                  name='pumpFrequency'
                  value={formatValue(formData.pumpFrequency)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 32 and 500 Hz'
                  error={touched.pumpFrequency ? errors.pumpFrequency : ''}
                  required={true}
                />
                <FormField
                  label='Pump discharge pressure (psig)'
                  name='pumpDischargePressure'
                  value={formatValue(formData.pumpDischargePressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1 and 5000 psig'
                  error={touched.pumpDischargePressure ? errors.pumpDischargePressure : ''}
                />
              </div>

              <div className='water-density-container'>
                <div className='water-density-field'>
                  <FormField
                    label='Water Density (s.g.)'
                    name='waterDensity'
                    value={formatValue(formData.waterDensity)}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    helpText='Must be between 0.39 and 1.6 stb/d'
                    containerClassName='water-density-input'
                    error={touched.waterDensity ? errors.waterDensity : ''}
                    required={true}
                  />
                </div>
              </div>

              <CommentSection value={formData.comment} onChange={(value) => handleInputChange('comment', value)} />

              {/* <div className='spacer' /> */}
            </section>
          </main>

          <footer className='form-footer'>
            <ActionButtons
              buttonText={buttonText}
              onSubmit={handleConfirmSave}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
            />
          </footer>
        </div>
      </div>
    </div>,
    document.querySelector('.outlet-container') || document.body,
  );
}
